import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { KeyRound } from 'lucide-react'
import { useSetPassword } from '@/hooks/useAuth'
import { ApiError } from '@/api/client'
import { tokenFromLocation } from '@/lib/linkToken'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Footer } from '@/components/Footer'
import { cn } from '@/lib/utils'

/**
 * The invitation link's landing page (`/set-password#token=…` — the fragment,
 * rule 8; every tricorder-users email with a purpose points here). The link
 * proves the address; this page collects the password and consumes it.
 *
 * There is no sign-up form anywhere else in the app: an account exists only
 * because someone was invited (or the address is allowlisted, whose door is
 * the provider sign-in on /login). The password + profile form people used to
 * meet at /register is this page, reached only by holding the emailed link.
 *
 * The refusals are the link's own states, and each says what happened and
 * what to do about it:
 *  - 403 with a session-less body: the password IS set, the account's period
 *    has not begun — the problem's detail names the start date, shown as-is
 *    (the Errors table is the platform's message contract).
 *  - 410: the link is dead (expired or a newer one was sent) — ask the team
 *    lead, or use the newest email.
 *  - 409: the password is already set — sign in.
 */
export default function SetPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setPassword = useSetPassword()
  const token = tokenFromLocation()

  const [password, setPasswordValue] = useState('')
  const [confirm, setConfirm] = useState('')

  const canSubmit =
    token !== '' && password.length >= 8 && password === confirm && !setPassword.isPending

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    try {
      await setPassword.mutateAsync({ token, password })
      // The hook persisted the session; /home's guard routes a
      // not-yet-onboarded account on to /onboarding.
      void navigate('/home', { replace: true })
    } catch {
      // Rendered below from setPassword.error — the branch itself lives in
      // failureCopy so the message and the hint stay one decision.
    }
  }

  /** The verdict for the link's own failure states, derived from the
   *  mutation's error each render; null keeps the form quiet (validation is
   *  the two fields' own hints, not a banner). */
  const failure = (() => {
    const err = setPassword.error
    if (!(err instanceof ApiError)) return null
    if (err.status === 403) {
      // The password IS set — the account cannot sign in until its period
      // begins, and the server's detail names when. Those words are the copy.
      return { message: err.message }
    }
    if (err.status === 410) {
      return {
        message: t('auth.setPassword.expired'),
        hint: t('auth.setPassword.expiredHint'),
      }
    }
    if (err.status === 409) {
      return { message: t('auth.setPassword.alreadySet') }
    }
    return { message: err.message }
  })()

  // A dead arrival: no token in the URL at all. Same screen as an expired
  // link — nothing here can proceed without the emailed proof.
  if (token === '') {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col">
        <div className="flex-1 flex flex-col justify-center px-6 py-12">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-error/10 flex items-center justify-center">
              <KeyRound className="h-8 w-8 text-error" />
            </div>
            <h1 className="text-2xl font-display font-semibold text-neutral-900 tracking-tight">
              {t('auth.setPassword.invalid.title')}
            </h1>
            <p className="mt-3 text-sm text-neutral-500">
              {t('auth.setPassword.invalid.description')}
            </p>
            <Link
              to="/login"
              className="inline-block mt-8 text-sm font-medium text-primary hover:text-primary-600"
            >
              {t('auth.setPassword.backToLogin')}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <div className="flex-1 flex flex-col justify-center px-6 py-12">
        <div className="w-full max-w-md mx-auto">
          <div className="mb-10 text-center">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 rounded-2xl bg-primary/20 animate-pulse-subtle" />
              <div className="absolute inset-1 rounded-xl bg-gradient-to-br from-primary to-primary-700 flex items-center justify-center shadow-lg shadow-primary/30">
                <KeyRound className="w-10 h-10 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-display font-semibold text-neutral-900 tracking-tight">
              {t('auth.setPassword.title')}
            </h1>
            <p className="mt-2 text-sm text-neutral-500">
              {t('auth.setPassword.description')}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-neutral-700">
                {t('auth.password')}
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPasswordValue(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')}
                className={cn(
                  'h-12 text-base bg-white border-neutral-200',
                  'focus:border-primary focus:ring-primary/20',
                  'placeholder:text-neutral-400',
                )}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="confirm" className="text-sm font-medium text-neutral-700">
                {t('auth.passwordRepeatPlaceholder')}
              </label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={t('auth.passwordRepeatPlaceholder')}
                className={cn(
                  'h-12 text-base bg-white border-neutral-200',
                  'focus:border-primary focus:ring-primary/20',
                  'placeholder:text-neutral-400',
                )}
              />
              {confirm !== '' && password !== confirm && (
                <p className="text-xs text-error">{t('auth.passwordMismatch')}</p>
              )}
            </div>

            {failure && (
              <div className="p-3 rounded-lg bg-error/10 border border-error/20">
                <p className="text-sm text-error">{failure.message}</p>
                {failure.hint && (
                  <p className="mt-1 text-xs text-neutral-500">{failure.hint}</p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={!canSubmit}
              className={cn(
                'w-full h-12 text-base font-medium mt-6',
                'bg-primary hover:bg-primary-600 active:bg-primary-700',
                'transition-colors duration-150',
                'disabled:opacity-50',
              )}
            >
              {setPassword.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner className="h-4 w-4" />
                  {t('common.loading')}
                </span>
              ) : (
                t('auth.setPassword.submit')
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-neutral-500">
            <Link
              to="/login"
              className="font-medium text-primary hover:text-primary-600 transition-colors"
            >
              {t('auth.setPassword.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  )
}
